/**
 * @fileoverview List View Module - Manages the display of property listings
 *               in a card-based list format with infinite scrolling/lazy loading.
 * @module ListViewModule
 */
const ListViewModule = (function() {
  /**
   * Simple logging utility specific to the ListViewModule.
   * @namespace Logger
   */
  const Logger = {
    /**
     * Gets the current timestamp in ISO format.
     * @returns {string} ISO timestamp string.
     */
    timestamp: function() {
      return new Date().toISOString();
    },
    /**
     * Logs a standard message.
     * @param {string} message - The message to log.
     */
    log: function(message) {
      // console.log(`[${this.timestamp()}] [ListViewModule] ${message}`);
    },
    /**
     * Logs an error message.
     * @param {string} message - The error description.
     * @param {*} [error] - Optional error object or details.
     */
    error: function(message, error) {
      // console.error(`[${this.timestamp()}] [ListViewModule] ERROR: ${message}`, error || '');
    },
    /**
     * Logs a warning message.
     * @param {string} message - The warning message.
     */
    warn: function(message) {
      // console.warn(`[${this.timestamp()}] [ListViewModule] WARNING: ${message}`);
    },
    /**
     * Logs an informational message.
     * @param {string} message - The info message.
     */
    info: function(message) {
      // console.info(`[${this.timestamp()}] [ListViewModule] INFO: ${message}`);
    },
    /**
     * Logs a debug message.
     * @param {string} message - The debug message.
     */
    debug: function(message) {
      // console.debug(`[${this.timestamp()}] [ListViewModule] DEBUG: ${message}`);
    }
  };


  /**
   * Current sort criteria ('initial', 'price_high', 'price_low', 'urlid_asc', 'urlid_desc').
   * @type {string}
   * @private
   */
  let currentSort = 'initial'; // Default sort is now 'initial' (random)

  /**
   * Flag to prevent URL update loops when applying sort from URL state.
   * @type {boolean}
   * @private
   */
  let updatingFromUrl = false;

  /**
   * Valid sort options for URL whitelist validation.
   * SECURITY: Only these values are accepted from URL parameters.
   * @type {string[]}
   * @const
   * @private
   */
  const VALID_SORT_OPTIONS = ['initial', 'price_high', 'price_low', 'urlid_asc', 'urlid_desc'];

  //======== URL STATE FUNCTIONS ========

  /**
   * Reads sort state from URL and applies to currentSort.
   * SECURITY: Uses whitelist validation via UrlState.getEnum().
   *
   * CONTEXT: Called on page load and on browser back/forward navigation.
   * UPSTREAM: Called by init() and UrlState.onChange() handler.
   * DOWNSTREAM: Updates currentSort, then UI is synced.
   *
   * @private
   */
  function readSortFromUrl() {
    currentSort = UrlState.getEnum('sort', VALID_SORT_OPTIONS, 'initial');
  }

  /**
   * Writes current sort to URL if it differs from default.
   * Only writes non-default values to keep URLs clean.
   *
   * CONTEXT: Called when user changes sort option.
   * UPSTREAM: Called from sort option click handler.
   * DOWNSTREAM: Updates browser URL via UrlState.set().
   *
   * @private
   */
  function writeSortToUrl() {
    if (updatingFromUrl) return;

    const params = {};
    // Only include sort in URL if it's not the default
    if (currentSort !== 'initial') {
      params.sort = currentSort;
    } else {
      params.sort = null; // Remove from URL if default
    }

    UrlState.set(params);
  }

  /**
   * Updates the sort dropdown UI to reflect the current sort state.
   * Updates both the main and toolbar sort dropdowns.
   *
   * @private
   */
  function syncSortDropdownUI() {
    const sortDropdown = document.getElementById('sortDropdown');
    const sortDropdownToolbar = document.getElementById('sortDropdownToolbar');

    // Find the matching sort option text
    const sortOption = document.querySelector(`.sort-option[data-sort="${currentSort}"]`);
    const sortText = sortOption ? sortOption.textContent : t('list.sortBy');

    if (sortDropdown) {
      sortDropdown.textContent = t('list.sortBy') + ': ' + sortText;
    }

    if (sortDropdownToolbar) {
      sortDropdownToolbar.innerHTML = `<i class="bi bi-sort-down"></i> ${sortText}`;
    }
  }

  /**
   * Current filter state object received from FilterModule.
   * @type {object}
   * @private
   */
  let currentFilter = {}; // Current filter state
  /**
   * Total number of items matching the current filter. Used for pagination logic.
   * @type {number}
   * @private
   */
  let totalCount = 0; // Total number of filtered items
  /**
   * The next page number to load for infinite scrolling (0-based).
   * @type {number}
   * @private
   */
  let currentPage = 0; // Current page for infinite scrolling
  /**
   * Flag to prevent multiple concurrent loads during infinite scrolling.
   * @type {boolean}
   * @private
   */
  let isLoading = false; // Flag to prevent multiple simultaneous loads
  /**
   * Base64 encoded SVG placeholder image for property cards.
   * @type {string}
   * @private
   */
  let placeholderImage = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzIwIiBoZWlnaHQ9IjE2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZjFmM2Y0Ii8+PHBhdGggZD0iTTE2MCw3NSBMMjIwLDEyMCBMMTAwLDEyMCBaIiBmaWxsPSIjZDJkNmRjIi8+PGNpcmNsZSBjeD0iMTkwIiBjeT0iNjAiIHI9IjE1IiBmaWxsPSIjZDJkNmRjIi8+PHRleHQgeD0iMTYwIiB5PSI5MCIgZm9udC1mYW1pbHk9IkFyaWFsIiBmb250LXNpemU9IjEyIiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBmaWxsPSIjNDQ0Ij5JbWFnZSB1bmF2YWlsYWJsZTwvdGV4dD48L3N2Zz4=';

  // --- Configuration Constants ---
  /**
   * Number of property cards to load initially.
   * @type {number}
   * @const
   * @private
   */
  const INITIAL_PAGE_SIZE = 20; // Initial batch size
  /**
   * Number of additional property cards to load each time the user scrolls near the bottom.
   * @type {number}
   * @const
   * @private
   */
  const LOAD_MORE_SIZE = 10; // Number of items to load on scroll
  /**
   * Distance in pixels from the bottom of the scrollable container that triggers loading more items.
   * @type {number}
   * @const
   * @private
   */
  const SCROLL_THRESHOLD = 200; // Pixels from bottom to trigger loading more

  /**
   * Extracts the hostname (domain) from a given URL string.
   * Returns an empty string if the URL is invalid or missing.
   * @param {string} url - The URL string.
   * @returns {string} The extracted domain name (e.g., "example.com") or an empty string.
   * @private
   */
  function extractDomain(url) {
    if (!url) return '';

    try {
      return new URL(url).hostname;
    } catch (e) {
      return '';
    }
  }

  /**
   * Adds UTM parameters to a URL for tracking purposes.
   * Handles URLs that already have query parameters.
   * @param {string} url - The original URL.
   * @returns {string} URL with UTM parameters added.
   * @private
   */
  function addUtmParameters(url) {
    if (!url) return '';

    try {
      const urlObj = new URL(url);
      urlObj.searchParams.set('utm_source', 'caribbeanhousehunt.com');
      urlObj.searchParams.set('utm_medium', 'referral');
      urlObj.searchParams.set('utm_campaign', 'caribbeanhousehunt');
      return urlObj.toString();
    } catch (e) {
      // If URL parsing fails, append parameters manually
      const separator = url.includes('?') ? '&' : '?';
      return `${url}${separator}utm_source=caribbeanhousehunt.com&utm_medium=referral&utm_campaign=caribbeanhousehunt`;
    }
  }

  /**
   * Builds the price display string for a property card.
   * Prioritizes USD, then NAF, then EUR. Formats numbers with commas.
   * Returns "Price on request" if no price is available.
   * @param {object} property - The property data object.
   * @returns {string} The formatted price string (e.g., "$ 1,200,000", "XCG 1,500", "Price on request").
   * @private
   */
  function buildPriceDisplay(property) {
    if (property.price_usd && property.price_usd !== 'NULL') {
      return `$ ${AppUtils.numberWithCommas(property.price_usd)}`; // Assumes AppUtils exists and has numberWithCommas
    } else if (property.price_naf && property.price_naf !== 'NULL') {
      return `XCG ${AppUtils.numberWithCommas(property.price_naf)}`; // Format XCG price
    } else if (property.price_eur && property.price_eur !== 'NULL') {
      return `€ ${AppUtils.numberWithCommas(property.price_eur)}`; // Format EUR price
    } else {
      return t('price.onRequest');
    }
  }

  /**
   * Creates the HTML string for a single property card based on the property data.
   * Uses Bootstrap card structure and includes image, title, price, features, description snippet, and realtor info.
   * @param {object} property - The property data object.
   * @returns {string} The HTML string representing the property card.
   * @private
   */
  function createPropertyCard(property) {
    const domain = extractDomain(property.url_page); // Extract domain for source display
    const price = buildPriceDisplay(property); // Get formatted price
    const bedrooms = property.bedrooms && property.bedrooms !== 'NULL' ? `${property.bedrooms} ${t('property.bedroomsAbbr')}` : '';
    // Translate property type
    const typeKey = 'propertyType.' + (property.property_type || 'property');
    const propertyType = t(typeKey, { fallback: AppUtils.capitalize(property.property_type) || t('propertyType.property') });
    
    // Build location display with fallback options
    let locationDisplay = '';
    if (property.street && property.street !== 'NULL') {
      locationDisplay = property.street;
    } else if (property.resort && property.resort !== 'NULL') {
      locationDisplay = property.resort;
    } else if (property.neighborhood && property.neighborhood !== 'NULL') {
      locationDisplay = property.neighborhood;
    }
    
    const propertyLabel = t('propertyType.property');
    return `
      <div class="col">
        <div class="card property-card h-100" data-urlid="${property.urlid || ''}" data-title="${(property.property_title || propertyLabel).replace(/"/g, '&quot;')}">
          <div class="card-header-row">
            <h5 class="card-title">${property.property_title || propertyLabel}</h5>
            <div class="card-share-wrapper">
              <button class="card-share-btn share-btn" title="${t('action.share')}" aria-label="${t('action.share')}">
                <i class="bi bi-share-fill"></i>
              </button>
              <div class="share-menu" style="display: none;">
                <button class="share-option" data-platform="facebook">
                  <i class="bi bi-facebook"></i> ${t('share.facebook')}
                </button>
                <button class="share-option" data-platform="whatsapp">
                  <i class="bi bi-whatsapp"></i> ${t('share.whatsapp')}
                </button>
                <button class="share-option" data-platform="email">
                  <i class="bi bi-envelope"></i> ${t('share.email')}
                </button>
                <button class="share-option" data-platform="copy">
                  <i class="bi bi-link-45deg"></i> ${t('share.copyLink')}
                </button>
              </div>
            </div>
          </div>
          <a href="${addUtmParameters(property.url_page)}" target="_blank" class="thumbnail-link" onclick="AppUtils.trackListingClick('${property.realtor_id || ''}')">
            <img src="${property.image_url ? '/map-assets/property-images/' + property.image_url : placeholderImage}"
                 class="card-img-top"
                 alt="${property.property_title || propertyLabel}"
                 loading="lazy"
                 onerror="this.onerror=null; this.src='${placeholderImage}';" />
              <div class="property-source thumbnail-source">${t('property.source')} ${domain}</div>
          </a>
          <div class="card-body">
            <div class="price-location-row">
              <p class="price-tag">${price}</p>
              ${locationDisplay ?
                `<span class="location-clickable map-popup-btn" data-property-id="${property.id}" data-lat="${property.lat}" data-lng="${property.lng}" title="${locationDisplay}"><i class="bi bi-map"></i> <span class="location-text">${locationDisplay}</span></span>` :
                (property.lat && property.lng && !isNaN(parseFloat(property.lat)) && !isNaN(parseFloat(property.lng))) ?
                `<span class="location-clickable map-popup-btn location-icon-only" data-property-id="${property.id}" data-lat="${property.lat}" data-lng="${property.lng}" title="${t('nav.map')}"><i class="bi bi-map"></i></span>` : ''
              }
            </div>
            <div class="property-features">
              ${bedrooms ? `<span><i class="bi bi-house-door"></i> ${bedrooms}</span>` : ''}
              <span><i class="bi bi-building"></i> ${propertyType}</span>
            </div>
            <p class="card-text mt-2">${property.description ? property.description.substring(0, 100) + (property.description.length > 100 ? '...' : '') : ''}</p>
            <p class="realtor-info text-muted mt-2"><small>${t('property.listedBy')} ${property.realtor_name || t('property.unknownRealtor')}</small></p>
            <div class="property-actions">
              <a href="${addUtmParameters(property.url_page)}" class="btn btn-primary w-100" target="_blank" onclick="AppUtils.trackListingClick('${property.realtor_id || ''}');">${t('action.viewDetails')}</a>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  /**
   * Loads the next batch of properties for infinite scrolling.
   * Fetches data from DataManager based on `currentFilter` and `currentSort`.
   * Appends the new property cards to the list. Manages loading state and spinner visibility.
   * Debounced by the `isLoading` flag.
   * @private
   */
  function loadMoreProperties() {

    if (isLoading) {
      return;
    }
    
    if (currentPage * LOAD_MORE_SIZE >= totalCount) {
      return;
    }
    
    isLoading = true;
    
    // Show the loading spinner
    const spinner = document.getElementById('load-more-spinner');
    if (spinner) {
      spinner.style.display = 'block';
    } else {
      Logger.warn('Load more spinner element not found');
    }
    
    // Get the next batch of properties using DataManager
    const options = {
      sortBy: currentSort,
      page: currentPage,
      pageSize: LOAD_MORE_SIZE
    };
    
    
    // Use setTimeout to prevent UI blocking and allow the loading spinner to render
    setTimeout(() => {
      const result = DataManager.selectItems(currentFilter, options);
      
      const nextBatch = result.items;
      
      const propertyList = document.getElementById('property-list');
      if (!propertyList) {
        Logger.error('Property list element not found');
        isLoading = false;
        return;
      }
      
      // Create and append new cards
      const fragment = document.createDocumentFragment();
      const tempDiv = document.createElement('div');
      
      nextBatch.forEach((property, index) => {
        tempDiv.innerHTML = createPropertyCard(property);
        fragment.appendChild(tempDiv.firstElementChild);
        tempDiv.innerHTML = '';
        
        if (index % 10 === 0 && index > 0) {
        }
      });
      
      propertyList.appendChild(fragment);
      
      // Update page counter
      currentPage++;
      
      // Hide loading spinner if we've loaded all items
      if (currentPage * LOAD_MORE_SIZE >= totalCount) {
        if (spinner) {
          spinner.style.display = 'none';
        }
      } else {
      }
      
      isLoading = false;
    }, 100);
  }

  /**
   * Sets up event listeners for map popup buttons on property cards.
   * Uses event delegation to handle dynamically created buttons.
   * @private
   */
  function setupMapPopupHandlers() {
    const propertyList = document.getElementById('property-list');
    if (!propertyList) {
      Logger.error('Property list element not found for map popup handlers');
      return;
    }
    
    propertyList.addEventListener('click', function(event) {
      if (event.target.closest('.map-popup-btn')) {
        const button = event.target.closest('.map-popup-btn');
        const propertyId = button.getAttribute('data-property-id');
        const lat = parseFloat(button.getAttribute('data-lat'));
        const lng = parseFloat(button.getAttribute('data-lng'));
        
        if (!isNaN(lat) && !isNaN(lng)) {
          showMapPopup(propertyId, lat, lng);
        } else {
          Logger.warn('Invalid coordinates for map popup', { propertyId, lat, lng });
        }
      }
    });
  }

  /**
   * Creates and shows an inline map popup for a specific property.
   * @param {string} propertyId - The property ID
   * @param {number} lat - Latitude coordinate
   * @param {number} lng - Longitude coordinate
   * @private
   */
  function showMapPopup(propertyId, lat, lng) {
    
    // Remove any existing popup
    closeMapPopup();
    
    // Create popup overlay
    const overlay = document.createElement('div');
    overlay.id = 'map-popup-overlay';
    overlay.className = 'map-popup-overlay';
    
    // Create popup content
    const popup = document.createElement('div');
    popup.className = 'map-popup';
    popup.innerHTML = `
      <div class="map-popup-header">
        <h5>${t('map.propertyLocation')}</h5>
        <button class="btn-close" aria-label="${t('action.close')}">
          <i class="bi bi-x-lg"></i>
        </button>
      </div>
      <div class="map-popup-content">
        <div id="popup-map" class="popup-map"></div>
      </div>
    `;
    
    overlay.appendChild(popup);
    document.body.appendChild(overlay);
    
    // Setup close handlers
    const closeBtn = popup.querySelector('.btn-close');
    closeBtn.addEventListener('click', closeMapPopup);
    
    overlay.addEventListener('click', function(event) {
      if (event.target === overlay) {
        closeMapPopup();
      }
    });
    
    document.addEventListener('keydown', function(event) {
      if (event.key === 'Escape') {
        closeMapPopup();
      }
    });
    
    // Initialize the map
    setTimeout(() => {
      initializePopupMap(lat, lng);
    }, 100);
  }

  /**
   * Initializes the Google Map inside the popup.
   * @param {number} lat - Latitude coordinate
   * @param {number} lng - Longitude coordinate
   * @private
   */
  function initializePopupMap(lat, lng) {
    const mapContainer = document.getElementById('popup-map');
    if (!mapContainer) {
      Logger.error('Popup map container not found');
      return;
    }
    
    try {
      const map = new google.maps.Map(mapContainer, {
        center: { lat, lng },
        zoom: 15,
        mapTypeId: 'roadmap',
        disableDefaultUI: true,
        zoomControl: true,
        gestureHandling: 'greedy'
      });
      
      // Add marker for the property
      new google.maps.Marker({
        position: { lat, lng },
        map: map,
        icon: {
          path: google.maps.SymbolPath.CIRCLE,
          fillColor: '#f7A100',
          fillOpacity: 1,
          strokeColor: '#ffffff',
          strokeWeight: 2,
          scale: 12
        }
      });
      
    } catch (error) {
      Logger.error('Error initializing popup map', error);
      mapContainer.innerHTML = `<div class="text-center p-4">${t('property.mapNotLoaded')}</div>`;
    }
  }

  /**
   * Closes and removes the map popup from the DOM.
   * @private
   */
  function closeMapPopup() {
    const overlay = document.getElementById('map-popup-overlay');
    if (overlay) {
      overlay.remove();
    }
  }

  /**
   * Sets up the event listener for infinite scrolling on the main list container.
   * Listens for scroll events on the `.list-main-content` element.
   * @private
   */
  function setupInfiniteScroll() {
    // The list-main-content is the element that actually scrolls (has overflow-y: auto)
    const listMainContent = document.querySelector('.list-main-content');
    
    if (!listMainContent) {
      Logger.error('Failed to find .list-main-content element for scroll event');
      return;
    }
    
    
    listMainContent.addEventListener('scroll', function() {
      const scrollPosition = listMainContent.scrollTop + listMainContent.clientHeight;
      const scrollHeight = listMainContent.scrollHeight;
      
      // If we're close to the bottom, load more properties
      if (scrollHeight - scrollPosition < SCROLL_THRESHOLD) {
        loadMoreProperties();
      }
    });
  }

  /**
   * Initializes or re-initializes the property list display.
   * Clears the current list, fetches the first page of properties based on
   * `currentFilter` and `currentSort`, displays them, updates the total count,
   * and resets pagination state. Handles loading and empty states.
   * Also manages the URL ID sort notification visibility.
   * @returns {int} the number of properties listed
   * @private
   */
  function initializePropertyList() {

    const propertyList = document.getElementById('property-list');
    const listLoading = document.getElementById('list-loading');
    const listEmpty = document.getElementById('list-empty');
    const urlidNotification = document.getElementById('urlid-sort-notification');

    if (!propertyList) {
      Logger.error('Property list element not found');
      return;
    }

    if (!listLoading) {
      Logger.warn('List loading element not found');
    }

    if (!listEmpty) {
      Logger.warn('List empty element not found');
    }

    // Show/hide URL ID sort notification based on current sort
    if (urlidNotification) {
      if (currentSort === 'urlid_asc' || currentSort === 'urlid_desc') {
        urlidNotification.style.display = 'block';
      } else {
        urlidNotification.style.display = 'none';
      }
    } else {
      Logger.warn('URL ID sort notification element not found');
    }

    // Show loading state
    propertyList.innerHTML = '';
    if (listLoading) listLoading.style.display = 'block';
    if (listEmpty) listEmpty.style.display = 'none';
    
    // Get the initial batch of properties using DataManager
    const options = {
      sortBy: currentSort,
      page: 0,
      pageSize: INITIAL_PAGE_SIZE
    };
    
    const result = DataManager.selectItems(currentFilter, options);
    
    const initialProperties = result.items;
    totalCount = result.totalCount;
    
    
    // Update count in the toolbar
    const countElement = document.getElementById('list-count-toolbar');
    if (countElement) {
      countElement.textContent = `${totalCount} ${t('list.propertiesFound')}`;
    } else {
      Logger.warn('List count toolbar element not found');
    }
    
    // Reset pagination
    currentPage = Math.ceil(INITIAL_PAGE_SIZE / LOAD_MORE_SIZE); // Calculate the next page based on what we've already loaded
    
    // Hide loading state
    if (listLoading) {
      listLoading.style.display = 'none';
    }
    
    // If no properties, show empty state
    if (totalCount === 0) {
      if (listEmpty) listEmpty.style.display = 'block';
      
      const spinner = document.getElementById('load-more-spinner');
      if (spinner) spinner.style.display = 'none';
      
      return 0;
    }
    
    // Show the initial batch of properties
    const cards = initialProperties.map((property, index) => {
      const card = createPropertyCard(property);
      if (index % 10 === 0 && index > 0) {
      }
      return card;
    });
    
    propertyList.innerHTML = cards.join('');
    
    // Show or hide the load more spinner
    const spinner = document.getElementById('load-more-spinner');
    if (spinner) {
      const showSpinner = totalCount > INITIAL_PAGE_SIZE;
      spinner.style.display = showSpinner ? 'block' : 'none';
    } else {
      Logger.warn('Load more spinner element not found');
    }
    
    return totalCount;
  }

  // Public interface of the ListViewModule
  return {
    /**
     * Initializes the ListViewModule. Sets up event listeners for sorting controls,
     * the 'Back to Map' button, infinite scrolling, and subscribes to data loading events.
     * @public
     */
    init: function() {

      // Read initial sort from URL (URL is source of truth)
      readSortFromUrl();
      syncSortDropdownUI();

      // Subscribe to URL changes for browser back/forward navigation
      UrlState.onChange(function(state) {
        const urlSort = state.sort || 'initial';
        if (VALID_SORT_OPTIONS.includes(urlSort) && urlSort !== currentSort) {
          updatingFromUrl = true;
          currentSort = urlSort;
          syncSortDropdownUI();
          // Only update list if data is loaded and we have a filter
          if (DataManager.isDataLoaded() && Object.keys(currentFilter).length > 0) {
            initializePropertyList();
          }
          updatingFromUrl = false;
        }
      });

      // Set up event listeners for sort dropdowns (both in main view and toolbar)
      const sortOptions = document.querySelectorAll('.sort-option');
      if (sortOptions.length === 0) {
        Logger.warn('No sort option elements found');
      } else {
      }

      sortOptions.forEach(option => {
        option.addEventListener('click', function() {
          const newSort = this.getAttribute('data-sort');

          // Only update if the sort option has changed
          if (currentSort !== newSort) {
            currentSort = newSort;

            // Update dropdown UI
            syncSortDropdownUI();

            // Update URL (source of truth)
            writeSortToUrl();

            // Update the list view
            initializePropertyList();
          } else {
          }
        });
      });
      
      // Set up event listener for the back to map button
      const backToMapBtn = document.getElementById('back-to-map-btn');
      if (!backToMapBtn) {
        Logger.warn('Back to map button element not found');
      } else {
        backToMapBtn.addEventListener('click', function() {
          if (typeof App !== 'undefined' && typeof App.switchView === 'function') {
            App.switchView(App.VIEW_STATE.MAP);
          } else {
            Logger.error('App module or switchView function not available');
          }
        });
      }
      
      // Setup infinite scroll
      setupInfiniteScroll();
      
      // Setup map popup handlers
      setupMapPopupHandlers();
      
      // Subscribe to data loaded event
      DataManager.subscribe('dataloaded', () => {
        if (Object.keys(currentFilter).length > 0) {
          initializePropertyList();
        } else {
        }
      });
      
    },

    /**
     * Updates the list view based on a new filter state provided by the FilterModule.
     * Stores the new filter state and re-initializes the property list display.
     * @param {object} filterState - The new filter state object.
     * @public
     */
    updateFilteredList: function(filterState) {
      
      currentFilter = filterState;
      return initializePropertyList();
    },

    /**
     * Gets the currently selected sort option.
     * @returns {string} The current sort criteria ('initial', 'price_high', 'price_low', 'urlid_asc', 'urlid_desc').
     * @public
     */
    getCurrentSort: function() {
      return currentSort;
    }
  };
})();
